import { index, modelOptions, mongoose, post, prop, PropType, Ref, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { E164PhoneNumber, getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    NotificationRecordFieldsWithDefaults,
    NotificationRecordSchemaTypes,
} from "../types/notificationRecord.d.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "notificationRecords" as const;

export type NotificationMessageType =
    | "warningDigest"
    | "optInRequest"
    | "subscriptionNotice"
    | "settingsConfirm"
    | "ack"
    | "help";

/** One row per outbound SMS — the audit trail and the source of each compose's `since` cursor. */
@index({ address: 1, sentAt: 1 })
@index({ address: 1, messageType: 1, sentAt: 1 })
@index({ warningRecordIds: 1 })
@index({ address: 1, includedOptOutNotice: 1, sentAt: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "notificationRecords" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<NotificationRecord>>(...addSchemaKeyHook(SCHEMA_KEY))
export class NotificationRecord extends SchemaBase<NotificationRecord, NotificationRecordFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** E.164 number the message was sent to. Keyed by number so the trail survives address churn. */
    @validate({ zodValidator: E164PhoneNumber })
    @prop({ type: () => String, required: true })
    public address!: string;

    @validate({
        zodValidator: z.enum(["warningDigest", "optInRequest", "subscriptionNotice", "settingsConfirm", "ack", "help"]),
    })
    @prop({ type: () => String, required: true })
    public messageType!: NotificationMessageType;

    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public sentAt!: Date;

    /** The exact text sent. */
    @validate({ zodValidator: z.string() })
    @prop({ type: () => String, required: true })
    public body!: string;

    /**
     * The Warning version document `_id`s this digest included — the next compose's delta scan
     * excludes these so an already-texted version is never re-texted. These are version doc ids,
     * not SCD `versionId`s (which are monotonic integers per record).
     */
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "Warning" }, PropType.ARRAY)
    public warningVersionDocIds?: Ref<unknown>[];

    /**
     * The SCD recordIds of the warning episodes this digest included. Episode-level provenance —
     * backs "which digests covered this episode" lookups via the multikey index.
     * No ref: recordIds are not Warning `_id`s, so these fields cannot be populated.
     */
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => mongoose.Types.ObjectId, required: false }, PropType.ARRAY)
    public warningRecordIds?: mongoose.Types.ObjectId[];

    /** Per included Warning version document `_id`, the mode it was flagged at. */
    @validate({
        zodValidator: z.record(z.string(), z.enum(["standard", "critical"])).optional(),
    })
    @trackMixed
    @prop({ type: () => mongoose.Schema.Types.Mixed, required: false, allowMixed: Severity.ALLOW })
    public notificationModeByVersionDocId?: Record<string, "standard" | "critical">;

    /** The greatest `versionValidFrom` this digest texted; seeds the next compose's scan floor. */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public lastIncludedWarningAt?: Date;

    /** Whether this message carried the "reply STOP" opt-out notice. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public includedOptOutNotice: boolean = false;

    /** Whether this message carried a spam-throttle notice. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public includedSpamThrottleNotice: boolean = false;

    static getters = {} as const satisfies Record<string, (self: NotificationRecord) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<NotificationRecordSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<NotificationRecord, NotificationRecordFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

NotificationRecord.dtoValidators = getZodSchemaFromClass(
    NotificationRecord,
) as unknown as DtoValidatorTypes<NotificationRecordSchemaTypes>;
